import { useState } from "react";
import { animated, useSpring } from "react-spring";
import { colors } from "../../constants/constants";

const DNAStrands = ({ strokeWidth, strandAppear, bridgesAppear }) => {
  return (
    <animated.svg
      {...{
        style: {
          width: "100%",
        },
        viewBox: "0 0 100 100",
      }}
    >
      <animated.path
        {...{
          id: "strand1",
          stroke: "white",
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 30,100 C 30,75 70,75 70,50 S 30,25 30,0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: strandAppear,
        }}
      />
      <animated.path
        {...{
          id: "strand2",
          stroke: colors.purple3,
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 70,100 C 70,75 30,75 30,50 S 70,25 70,0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: strandAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-1-center",
          stroke: "white",
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 70,50 l-20 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-2-center",
          stroke: colors.purple3,
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 30,50 l20 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-1-aboveCenter",
          stroke: "white",
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 65,38 l-18 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-2-belowCenter",
          stroke: colors.purple3,
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 35,62 l18 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-1-low",
          stroke: "white",
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 35,87 l18 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
      <animated.path
        {...{
          id: "bridge-strand-2-high",
          stroke: colors.purple3,
          strokeWidth: strokeWidth,
          fill: "none",
          d: "M 65,13 l-18 0",
          pathLength: 1,
          strokeDasharray: 1,
          strokeDashoffset: bridgesAppear,
        }}
      />
    </animated.svg>
  );
};

let AnimatedDNAStrandsTimeline = {
  enterSlideUp: { at: 0, duration: 1000 },
  pushStrand: { at: 0, duration: 2000 },
  strandAppear: { at: 0, duration: 1500 },
  bridgesAppear: { at: 0, duration: 500 },
};
AnimatedDNAStrandsTimeline.bridgesAppear.at =
  AnimatedDNAStrandsTimeline.strandAppear.at +
  AnimatedDNAStrandsTimeline.strandAppear.duration +
  500;
AnimatedDNAStrandsTimeline.pushStrand.at =
  AnimatedDNAStrandsTimeline.enterSlideUp.at +
  AnimatedDNAStrandsTimeline.enterSlideUp.duration +
  2000;

const AnimatedDNAStrands = ({ active }) => {
  const strokeWidth = 3;
  const [enterSlideUpDone, setEnterSlideUpDone] = useState(false);
  const { enterSlideUp } = useSpring({
    from: { enterSlideUp: "100%" },
    to: { enterSlideUp: "0%" },
    config: { duration: AnimatedDNAStrandsTimeline.enterSlideUp.duration },
    delay: AnimatedDNAStrandsTimeline.enterSlideUp.at,
    onRest: () => setEnterSlideUpDone(true),
  });
  const { pushStrand } = useSpring({
    from: { pushStrand: "rotate(45deg) translate(0px, 0%)" },
    to: { pushStrand: "rotate(45deg) translate(0px, -33.333%)" },
    config: {
      duration: active
        ? AnimatedDNAStrandsTimeline.pushStrand.duration
        : AnimatedDNAStrandsTimeline.pushStrand.duration * 2,
    },
    delay: enterSlideUpDone ? 0 : AnimatedDNAStrandsTimeline.pushStrand.at,
    loop: true,
  });
  const { strandAppear } = useSpring({
    from: { strandAppear: 1 },
    to: { strandAppear: 0 },
    config: { duration: AnimatedDNAStrandsTimeline.strandAppear.duration },
    delay: AnimatedDNAStrandsTimeline.strandAppear.at,
  });
  const { bridgesAppear } = useSpring({
    from: { bridgesAppear: 1 },
    to: { bridgesAppear: 0 },
    config: { duration: AnimatedDNAStrandsTimeline.bridgesAppear.duration },
    delay: AnimatedDNAStrandsTimeline.bridgesAppear.at,
  });
  return (
    <div
      {...{
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "0px",
          overflow: "hidden",
          borderRadius: "999px",
          width: "66%",
          height: "66%",
        },
      }}
    >
      <animated.div
        {...{
          style: {
            marginTop: enterSlideUp,
            transform: pushStrand,
          },
        }}
      >
        {enterSlideUpDone && (
          <DNAStrands {...{ strokeWidth, strandAppear, bridgesAppear }} />
        )}
        <DNAStrands {...{ strokeWidth, strandAppear, bridgesAppear }} />
        {enterSlideUpDone && (
          <DNAStrands {...{ strokeWidth, strandAppear, bridgesAppear }} />
        )}
      </animated.div>
    </div>
  );
};

let timeline: any = {
  arrow: { appears: {} },
  inner3WhitePath: { expands: {}, opens: {} },
  inner0WhiteCircle: { opens: {} },
  purpleArrow: { mainExpands: {}, sidesExpand: {} },
};

timeline.start = 0;
timeline.rotationSpeed = 2000;

timeline.arrow.appears.at = 1000;
timeline.arrow.appears.duration = 1000;

timeline.inner3WhitePath.expands.at = 500;
timeline.inner3WhitePath.expands.duration = 1000;
timeline.inner3WhitePath.opens.at =
  timeline.inner3WhitePath.expands.at +
  timeline.inner3WhitePath.expands.duration;
timeline.inner3WhitePath.opens.duration = 1000;

timeline.inner0WhiteCircle.opens.at =
  timeline.inner3WhitePath.expands.at +
  timeline.inner3WhitePath.expands.duration -
  500;
timeline.inner0WhiteCircle.opens.duration = 1000;

timeline.purpleArrow.mainExpands.at = timeline.inner0WhiteCircle.opens.at + 500;
timeline.purpleArrow.mainExpands.duration = 1000;
timeline.purpleArrow.sidesExpand.at =
  timeline.purpleArrow.mainExpands.at +
  timeline.purpleArrow.mainExpands.duration;
timeline.purpleArrow.sidesExpand.duration = 300;

const AnimatedPerf = ({ active, slideComponentUp = true }) => {
  const strokeWidth = 3;
  const { inner0WhiteCircleDashoffset } = useSpring({
    from: { inner0WhiteCircleDashoffset: 0 },
    to: { inner0WhiteCircleDashoffset: 0.4 },
    delay: timeline.inner0WhiteCircle.opens.at,
    config: { duration: timeline.inner0WhiteCircle.opens.duration },
  });
  const { inner1WhiteCircleRotation } = useSpring({
    from: { inner1WhiteCircleRotation: "rotate(0, 50 50)" },
    to: { inner1WhiteCircleRotation: "rotate(360, 50 50)" },
    config: {
      duration: active ? timeline.rotationSpeed : timeline.rotationSpeed * 2,
    },
    loop: true,
  });
  const { inner2PurpleCircleRotation } = useSpring({
    from: { inner2PurpleCircleRotation: "rotate(360, 50 50)" },
    to: { inner2PurpleCircleRotation: "rotate(0, 50 50)" },
    config: {
      duration: active ? timeline.rotationSpeed : timeline.rotationSpeed * 2,
    },
    loop: true,
  });
  const { inner3WhitePathDashoffset } = useSpring({
    from: { inner3WhitePathDashoffset: 1 },
    to: { inner3WhitePathDashoffset: 0 },
    delay: timeline.inner3WhitePath.expands.at,
    config: { duration: timeline.purpleArrow.mainExpands.duration },
  });
  const { purpleArrowPathDashoffset } = useSpring({
    from: { purpleArrowPathDashoffset: 1 },
    to: { purpleArrowPathDashoffset: 0 },
    config: { duration: timeline.purpleArrow.mainExpands.duration },
    delay: timeline.purpleArrow.mainExpands.at,
  });
  const { purpleArrowSidesPathDashoffset } = useSpring({
    from: { purpleArrowSidesPathDashoffset: 1 },
    to: { purpleArrowSidesPathDashoffset: 0 },
    config: { duration: timeline.purpleArrow.sidesExpand.duration },
    delay: timeline.purpleArrow.sidesExpand.at,
  });
  const { purpleArrowMaskExpand } = useSpring({
    from: { purpleArrowMaskExpand: "50 0 50 0 50 50" },
    to: { purpleArrowMaskExpand: "35 0 65 0 50 50" },
    config: { duration: timeline.inner3WhitePath.opens.duration },
    delay: timeline.inner3WhitePath.opens.at,
  });
  const { slideUp } = useSpring({
    from: { slideUp: "100%" },
    to: { slideUp: "0%" },
    delay: 0,
    config: {
      friction: 200,
    },
  });
  return (
    <div
      {...{
        style: {
          height: "66%",
          width: "66%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          borderRadius: "999px",
        },
      }}
    >
      <animated.div
        {...{
          style: {
            paddingTop: slideUp,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <animated.svg
          style={{
            width: "100%",
            marginTop: "0%",
          }}
          viewBox="0 0 100 100"
        >
          {/* mask for inner 1 circle */}
          <mask id="inner1CircleMask">
            <rect
              {...{
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                fill: "white",
              }}
            />
            <rect
              {...{
                x: 0,
                y: 40,
                width: 100,
                height: 60,
                fill: "black",
              }}
            />
            <rect
              {...{
                x: 50,
                y: 0,
                width: 50,
                height: 50,
                fill: "black",
              }}
            />
            <rect
              {...{
                x: 28,
                y: 35,
                width: 10,
                height: 10,
                fill: "white",
              }}
            />
          </mask>
          {/* inner 0 circle (0 = most)*/}
          <animated.circle
            {...{
              cx: 50,
              cy: 50,
              r: 10,
              stroke: "white",
              strokeWidth,
              fill: "none",
              pathLength: 1,
              strokeDasharray: 1,
              strokeDashoffset: inner0WhiteCircleDashoffset,
            }}
          />
          {/* inner 1 circle */}
          <g {...{ mask: "url(#inner1CircleMask)" }}>
            <animated.circle
              {...{
                cx: 50,
                cy: 50,
                r: 20,
                stroke: "white",
                strokeWidth,
                fill: "none",
                pathLength: 100,
                strokeDasharray: "20 30",
                transform: inner1WhiteCircleRotation,
              }}
            />
          </g>
          {/* inner 2 circle */}
          <g {...{ mask: "url(#inner1CircleMask)" }}>
            <animated.circle
              {...{
                cx: 50,
                cy: 50,
                r: 30,
                stroke: colors.purple3,
                strokeWidth,
                fill: "none",
                pathLength: 100,
                strokeDasharray: "44 6",
                transform: inner2PurpleCircleRotation,
              }}
            />
          </g>
          <animated.path
            {...{
              mask: "url(#purpleArrowMask)",
              stroke: "white",
              strokeWidth,
              fill: "none",
              // a rx ry x-axis-rotation large-arc-flag sweep-flag dx dy
              d: "M20 50 L10 50 a 40 40 0 0 1 80 0 l -10 0",
              pathLength: 1,
              strokeDasharray: 1,
              strokeDashoffset: inner3WhitePathDashoffset,
            }}
          />

          {/* arrow mask */}
          <mask id="purpleArrowMask">
            <rect
              {...{
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                fill: "white",
              }}
            />
            <animated.polygon
              {...{
                points: purpleArrowMaskExpand,
                // points: `${50 - purpleArrowMaskExpand} 0 ${50 + purpleArrowMaskExpand} 0 50 50`,
                fill: "black",
                transform: "rotate(30, 50, 50)",
              }}
            />
          </mask>

          {/* purple arrow */}
          <g {...{ transform: "rotate(30, 50, 50)" }}>
            <animated.path
              {...{
                stroke: colors.purple3,
                strokeWidth,
                fill: "none",
                d: "M50 50 L50 5",
                pathLength: 1,
                strokeDasharray: 1,
                strokeDashoffset: purpleArrowPathDashoffset,
              }}
            />
            {/* arrow head */}
            <animated.path
              {...{
                stroke: colors.purple3,
                strokeWidth,
                fill: "none",
                d: "M50 5 L45 10",
                pathLength: 1,
                strokeDasharray: 1,
                strokeLinecap: "round",
                strokeDashoffset: purpleArrowSidesPathDashoffset,
              }}
            />
            <animated.path
              {...{
                stroke: colors.purple3,
                strokeWidth,
                fill: "none",
                d: "M50 5 L55 10",
                pathLength: 1,
                strokeDasharray: 1,
                strokeLinecap: "round",
                strokeDashoffset: purpleArrowSidesPathDashoffset,
              }}
            />
          </g>
        </animated.svg>
      </animated.div>
    </div>
  );
};

const AnimatedMedia = ({ active }) => {
  const [appearDone, setAppearDone] = useState(false);
  const { offset } = useSpring({
    from: { offset: 100 },
    to: { offset: 0 },
    delay: 0,
    config: {
      duration: active ? 5000 : 10000,
    },
    loop: true,
  });
  const { appear } = useSpring({
    from: { appear: 100 },
    to: { appear: 0 },
    delay: 0,
    config: {
      duration: 2000,
    },
    onRest: () => setAppearDone(true),
  });
  const { slideUp } = useSpring({
    from: { slideUp: "100%" },
    to: { slideUp: "0%" },
    delay: 0,
    config: {
      friction: 200,
    },
  });

  return (
    <animated.div
      {...{
        style: {
          height: "66%",
          width: "66%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          borderRadius: "999px",
        },
      }}
    >
      <animated.div
        {...{
          style: {
            paddingTop: slideUp,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <svg
          style={{
            width: "70%",
          }}
          viewBox="0 0 108 112"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <animated.path
            d="M77 67L29.3243 98.9427C27.809 99.958 26.0262 100.5 24.2022 100.5V100.5C19.12 100.5 15 96.38 15 91.2978V86.5"
            stroke={colors.purple3}
            strokeWidth="4"
            pathLength="100"
          />
          {!appearDone && (
            <animated.path
              d="M2.5 48V17.7787C2.5 13.4916 4.43833 9.43442 7.7734 6.74072V6.74072C12.4126 2.99365 18.8998 2.54737 24.0084 5.62385L100.742 51.8336C104.005 53.7985 106 57.329 106 61.1378V61.1378C106 64.7965 104.158 68.2093 101.099 70.2172L43.2895 108.169C41.4696 109.363 39.3401 110 37.1632 110V110C30.9979 110 26 105.002 26 98.8368V39.5"
              stroke="white"
              strokeWidth="4"
              pathLength="100"
              strokeDasharray="100"
              strokeDashoffset={appear}
            />
          )}
          {appearDone && (
            <animated.path
              d="M2.5 48V17.7787C2.5 13.4916 4.43833 9.43442 7.7734 6.74072V6.74072C12.4126 2.99365 18.8998 2.54737 24.0084 5.62385L100.742 51.8336C104.005 53.7985 106 57.329 106 61.1378V61.1378C106 64.7965 104.158 68.2093 101.099 70.2172L43.2895 108.169C41.4696 109.363 39.3401 110 37.1632 110V110C30.9979 110 26 105.002 26 98.8368V39.5"
              stroke="white"
              strokeWidth="4"
              pathLength="100"
              strokeDasharray="45 1 2 1 50 0"
              strokeDashoffset={offset}
            />
          )}
        </svg>
      </animated.div>
    </animated.div>
  );
};

const AnimatedPreorder = ({ active }) => {
  const { slideUp } = useSpring({
    from: { slideUp: "100%" },
    to: { slideUp: "0%" },
    delay: 0,
    config: {
      // duration: 2000,
      friction: 200,
    },
  });

  return (
    <animated.div
      {...{
        style: {
          height: "66%",
          width: "66%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          borderRadius: "999px",
        },
      }}
    >
      <animated.div
        {...{
          style: {
            paddingTop: slideUp,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <Preorder {...{ active }} />
      </animated.div>
    </animated.div>
  );
};

const Preorder = ({ active }) => {
  const outsideSecondPath =
    "M74.8827 37.8901C81.5549 42.1981 90.488 39.8044 94.1123 32.7375C97.6167 25.9045 107.383 25.9045 110.888 32.7375C114.512 39.8044 123.445 42.1981 130.117 37.8901C136.569 33.7247 145.027 38.608 144.645 46.2777C144.25 54.21 150.79 60.7495 158.722 60.3548C166.392 59.9732 171.275 68.4313 167.11 74.8827C162.802 81.5549 165.196 90.488 172.263 94.1123C179.096 97.6167 179.096 107.383 172.263 110.888C165.196 114.512 162.802 123.445 167.11 130.117C171.275 136.569 166.392 145.027 158.722 144.645C150.79 144.25 144.25 150.79 144.645 158.722C145.027 166.392 136.569 171.275 130.117 167.11C123.445 162.802 114.512 165.196 110.888 172.263C107.383 179.096 97.6167 179.096 94.1123 172.263C90.488 165.196 81.5549 162.802 74.8827 167.11C68.4313 171.275 59.9732 166.392 60.3548 158.722C60.7495 150.79 54.21 144.25 46.2777 144.645C38.608 145.027 33.7247 136.569 37.8901 130.117C42.1981 123.445 39.8044 114.512 32.7375 110.888C25.9045 107.383 25.9045 97.6167 32.7375 94.1123C39.8044 90.488 42.1981 81.5549 37.8901 74.8827C33.7247 68.4313 38.608 59.9732 46.2777 60.3548C54.21 60.7495 60.7495 54.21 60.3548 46.2777C59.9732 38.608 68.4313 33.7247 74.8827 37.8901Z";
  const outsideMostPath =
    "M69.8972 23.473C77.4828 26.5571 86.1941 24.223 91.2214 17.7592C96.9418 10.4041 108.058 10.4041 113.779 17.7592C118.806 24.223 127.517 26.5571 135.103 23.473C143.734 19.9635 153.361 25.5217 154.638 34.7516C155.76 42.8631 162.137 49.2401 170.248 50.362C179.478 51.6385 185.036 61.2656 181.527 69.8972C178.443 77.4828 180.777 86.1941 187.241 91.2214C194.596 96.9418 194.596 108.058 187.241 113.779C180.777 118.806 178.443 127.517 181.527 135.103C185.036 143.734 179.478 153.361 170.248 154.638C162.137 155.76 155.76 162.137 154.638 170.248C153.361 179.478 143.734 185.036 135.103 181.527C127.517 178.443 118.806 180.777 113.779 187.241C108.058 194.596 96.9418 194.596 91.2214 187.241C86.1941 180.777 77.4828 178.443 69.8972 181.527C61.2656 185.036 51.6385 179.478 50.362 170.248C49.2401 162.137 42.8631 155.76 34.7516 154.638C25.5217 153.361 19.9635 143.734 23.473 135.103C26.5571 127.517 24.223 118.806 17.7592 113.779C10.4041 108.058 10.4041 96.9418 17.7592 91.2214C24.223 86.1941 26.5571 77.4828 23.473 69.8972C19.9635 61.2656 25.5217 51.6385 34.7516 50.362C42.8631 49.2401 49.2401 42.8631 50.362 34.7516C51.6385 25.5217 61.2656 19.9635 69.8972 23.473Z";
  const innerSecondPath =
    "M80.4912 52.9965C85.8709 58.032 94.6672 55.6751 96.8084 48.6243C98.5171 42.9979 106.483 42.9979 108.192 48.6243C110.333 55.6751 119.129 58.032 124.509 52.9965C128.802 48.9782 135.7 52.9611 134.367 58.688C132.696 65.8648 139.135 72.3041 146.312 70.6331C152.039 69.2997 156.022 76.1983 152.004 80.4912C146.968 85.8709 149.325 94.6672 156.376 96.8084C162.002 98.5171 162.002 106.483 156.376 108.192C149.325 110.333 146.968 119.129 152.004 124.509C156.022 128.802 152.039 135.7 146.312 134.367C139.135 132.696 132.696 139.135 134.367 146.312C135.7 152.039 128.802 156.022 124.509 152.004C119.129 146.968 110.333 149.325 108.192 156.376C106.483 162.002 98.5171 162.002 96.8084 156.376C94.6672 149.325 85.8709 146.968 80.4912 152.004C76.1983 156.022 69.2997 152.039 70.6331 146.312C72.3041 139.135 65.8648 132.696 58.688 134.367C52.9611 135.7 48.9782 128.802 52.9965 124.509C58.032 119.129 55.6751 110.333 48.6243 108.192C42.9979 106.483 42.9979 98.5171 48.6243 96.8084C55.6751 94.6672 58.032 85.8709 52.9965 80.4912C48.9782 76.1983 52.9611 69.2997 58.688 70.6331C65.8648 72.3041 72.3041 65.8648 70.6331 58.688C69.2997 52.9611 76.1983 48.9782 80.4912 52.9965Z";
  const { inner1 } = useSpring({
    from: { inner1: "rotateZ(0deg)" },
    to: { inner1: "rotateZ(360deg)" },
    config: { duration: active ? 20000 / 2 : 20000 },
    loop: true,
  });
  const { inner2 } = useSpring({
    from: { inner2: "rotateZ(0deg)" },
    to: { inner2: "rotateZ(360deg)" },
    config: { duration: active ? 22000 / 2 : 22000 },
    loop: true,
  });
  const { inner3 } = useSpring({
    from: { inner3: "rotateZ(0deg)" },
    to: { inner3: "rotateZ(360deg)" },
    config: { duration: active ? 24000 / 2 : 24000 },
    loop: true,
  });
  const [flip1, set1] = useState(false);
  const [flip2, set2] = useState(false);
  const [flip3, set3] = useState(false);
  const { color1 } = useSpring({
    from: { color1: colors.purple3 },
    to: { color1: "#ffffff" },
    config: { duration: active ? 5000 / 2 : 5000 },
    reverse: flip1,
    onRest: () => set1(!flip1),
  });
  const { color2 } = useSpring({
    from: { color2: colors.purple3 },
    to: { color2: "#ffffff" },
    config: { duration: active ? 6000 / 2 : 6000 },
    reverse: flip2,
    onRest: () => set2(!flip2),
  });
  const { color3 } = useSpring({
    from: { color3: colors.purple3 },
    to: { color3: "#ffffff" },
    config: { duration: active ? 7000 / 2 : 7000 },
    reverse: flip3,
    onRest: () => set3(!flip3),
  });
  const strokeWidth = 6;
  return (
    <animated.svg
      width="100%"
      viewBox="0 0 205 205"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <animated.path
        style={{
          transform: inner1,
          transformOrigin: "center",
          stroke: color3,
          strokeWidth,
        }}
        d={innerSecondPath}
      />
      <g transform="translate(0, 0)">
        <animated.path
          style={{
            transform: inner2,
            transformOrigin: "center",
            stroke: color2,
          }}
          d={outsideSecondPath}
          strokeWidth={strokeWidth}
        />
      </g>
      <animated.path
        style={{
          transform: inner3,
          transformOrigin: "center",
          stroke: color1,
          strokeWidth,
        }}
        d={outsideMostPath}
      />
    </animated.svg>
  );
};

export {
  AnimatedDNAStrands,
  DNAStrands,
  AnimatedPerf,
  AnimatedMedia,
  AnimatedPreorder,
};
